import { CaretDownIcon } from "@phosphor-icons/react/dist/csr/CaretDown";
import { CheckIcon } from "@phosphor-icons/react/dist/csr/Check";
import { Button } from "@volca/ui/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@volca/ui/components/ui/dropdown-menu";
import { periodNoun } from "@volca/ui/trades";
import { dayjs } from "@/core/utils/date";
import { INLINE_CONTROL } from "@/profitability/constants";
import { formatReportPeriod } from "@/profitability/utils";
import type { CompareOption, OverviewPeriod } from "./types";

/**
 * What the period is compared with, as one plain choice in the sentence
 * under its name: last week (the default), a 4-week average that smooths an
 * odd week, the same week last year, or a week you pick. Presets rather than
 * a free picker, for owners who would rather not build a comparison.
 */
export const CompareSelect = ({
  label,
  value,
  options,
  periods,
  current,
  onChange,
}: {
  /** What it is compared with now: "Oct 5–11, 2026", "4-week average". */
  label: string;
  value: string;
  options: CompareOption[];
  periods: OverviewPeriod[];
  current: string;
  onChange: (compare: string | null) => void;
}) => {
  const others = periods.filter((period) => period.run_id !== current);
  // What a period is called, from how long the one on screen runs.
  const shown = periods.find((period) => period.run_id === current);
  const noun = shown
    ? periodNoun(dayjs(shown.end_date).diff(shown.start_date, "day") + 1)
    : "period";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            variant="link"
            color="neutral"
            size="sm"
            className={INLINE_CONTROL}
          />
        }
      >
        {label}
        <CaretDownIcon aria-hidden className="size-3" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-56">
        {options.map((option) => (
          <DropdownMenuItem
            key={option.value}
            disabled={!option.available}
            onClick={() =>
              onChange(option.value === "previous" ? null : option.value)
            }
            className="justify-between gap-4"
          >
            {option.label}
            {value === option.value && (
              <CheckIcon aria-hidden className="size-3.5" />
            )}
          </DropdownMenuItem>
        ))}
        {others.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                {`A ${noun} you pick`}
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="max-h-72 whitespace-nowrap">
                {others.map((period) => (
                  <DropdownMenuItem
                    key={period.run_id}
                    onClick={() => onChange(`run:${period.run_id}`)}
                    className="justify-between gap-4 tabular-nums"
                  >
                    {formatReportPeriod(period)}
                    {value === `run:${period.run_id}` && (
                      <CheckIcon aria-hidden className="size-3.5" />
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
